import React, { FC, memo, useCallback } from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n, useLicenses } from '~Hooks';
import { useEposTheme } from '../../Providers/ThemeProvider';
import StepFrame from './StepFrame';

/** The screen resets by itself after this, so the next shopper is not met by the last order. */
const AUTO_FINISH_MS = 10000;

/** Wraps the step so the timer bar can sit flush along the card's bottom edge. */
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

/** Track along the bottom of the card. */
const Track = styled.div`
  height: 10px;
  background: var(--eposBorderColor);
`;

/**
 * Fills left to right in the theme's button color over AUTO_FINISH_MS. When it
 * reaches the right edge the register starts over - the animation end is the
 * timer, so what the shopper sees and what happens can never drift apart.
 */
const Fill = styled.div`
  height: 100%;
  width: 0;
  background: var(--eposButton);
  animation: eposDoneTimer ${AUTO_FINISH_MS}ms linear forwards;

  @keyframes eposDoneTimer {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }
`;

/**
 * The order number, big enough to read from the pickup counter. Always
 * rendered at full size - a number is always coming, so the box holds its
 * place while it is still on its way and the step never jumps when it lands.
 */
const Number = styled.div`
  min-width: 360px;
  min-height: 160px;
  padding: 24px 56px;
  border-radius: 32px;
  box-sizing: content-box;
  font-size: 160px;
  font-weight: 800;
  letter-spacing: -4px;
  line-height: 1;
  background: var(--colorBlueGray100);
  color: var(--eposText);
  font-variant-numeric: tabular-nums;
`;

const NewOrderButton = styled.button`
  appearance: none;
  min-width: 320px;
  min-height: 88px;
  padding: 0 40px;
  border-radius: 24px;
  border: 2px solid var(--eposBorderColor);
  cursor: pointer;
  font-family: var(--eposFontFamily);
  font-size: 24px;
  font-weight: 700;
  background: transparent;
  color: var(--eposText);
  transition: transform 0.15s ease-out;

  &.is-touching {
    transform: scale(0.97);
  }
`;

interface Props {
  orderNumber: string | null;
  onFinish: () => void;
}

/** "Here's your ticket!" with the order number; finishes on tap or after a while. */
const DoneStep: FC<Props> = ({ orderNumber, onFinish }) => {
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const eposTheme = useEposTheme();

  const { touching, disabled, disabledToast, ...button } = useButton({ onClick: onFinish });

  /** The bar reached the right edge. */
  const handleTimerEnd = useCallback(() => onFinish(), [onFinish]);

  return (
    <Wrapper>
      <StepFrame
        testId="epos-done-step"
        title={i18n('SSK.OrderNumber.Title')}
        subtitle={
          eposTheme.exitMessage
            ? i18n(eposTheme.exitMessage)
            : licenses.atleastOneDeviceHasCustomerDisplay
            ? i18n('SSK.OrderNumber.SubTitle.OrderDisplay')
            : i18n('SSK.OrderNumber.Subtitle.Generic')
        }
        actions={
          <NewOrderButton
            {...button}
            className={touching ? 'is-touching' : undefined}
            data-testid="epos-new-order">
            {i18n('Express.OrderNumber.NewOrder')}
          </NewOrderButton>
        }>
        <Number data-testid="epos-order-number">{orderNumber?.padStart(3, '0')}</Number>
      </StepFrame>
      <Track data-testid="epos-done-timer">
        <Fill onAnimationEnd={handleTimerEnd} />
      </Track>
    </Wrapper>
  );
};

export default memo(DoneStep);
